// Helpers for the Admin "Mail-Template Coverage Matrix" page.
// Pure functions kept separate from the React component so they can be tested
// and reasoned about in isolation.

// The single language the matrix reports on. All live-existence checks and
// template-content fetches are scoped to Dutch (see plan / TemplateConfig usage).
export const MATRIX_LANGUAGE = "nl";

export interface MatrixPath {
  automation: string;
  event: string;
  action: string;
  target: string;
}

// A flat, ordered list of things to render as rows: automation/event group
// headers interleaved with the individual path rows underneath them. The order
// mirrors the nesting walk in src/pages/mail-templates/Root.tsx so the matrix
// lines up with the rest of the app.
export type MatrixRow =
  | { kind: "automation"; automation: string; key: string }
  | { kind: "event"; automation: string; event: string; key: string }
  | { kind: "path"; path: MatrixPath; key: string };

// event/action/target uniquely identify a template config (automation is only a
// grouping), so the cell key omits the automation.
export function pathKey(p: { event: string; action: string; target: string }): string {
  return `${p.event}|${p.action}|${p.target}`;
}

// The key used by the mail-template "sends" payload (distinct separator, and a
// special case for INVALID_DATA — mirrors Template.tsx / Summary.tsx).
export function sendsPathKey(p: { event: string; action: string; target: string }): string {
  return p.event === "INVALID_DATA" ? "INVALID_DATA" : `${p.event}__${p.action}__${p.target}`;
}

// "YYYY-MM-DD" of the day `days` before `now` (Date normalises month/year
// underflow, so it's safe across month and year boundaries).
export function dateNDaysAgo(now: Date, days: number): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// A sends map is `{ "YYYY-MM-DD": count }`. True when any dated bucket on or after
// `cutoff` (a YYYY-MM-DD string) has a non-zero count. Same-format ISO date
// strings compare correctly with a plain lexicographic `>=`.
export function wasSentSince(sendData: Record<string, number> | undefined, cutoff: string): boolean {
  if (!sendData) return false;
  return Object.entries(sendData).some(([date, count]) => count > 0 && date.slice(0, 10) >= cutoff);
}

export function flattenEventPaths(events: any): MatrixRow[] {
  const rows: MatrixRow[] = [];
  Object.keys(events).forEach((automation) => {
    rows.push({ kind: "automation", automation, key: `a:${automation}` });
    Object.keys(events[automation]).forEach((event) => {
      rows.push({ kind: "event", automation, event, key: `e:${automation}:${event}` });
      Object.keys(events[automation][event]).forEach((action) => {
        events[automation][event][action].forEach((target: string) => {
          const path = { automation, event, action, target };
          rows.push({ kind: "path", path, key: `p:${pathKey(path)}` });
        });
      });
    });
  });
  return rows;
}

// The shape returned by fetchMailTemplateSummary(): two loosely-typed arrays.
export interface MailTemplateSummary {
  maintained: any[];
  config: any[];
}

// Whether the path is switched on (language-agnostic) and whether a non-empty
// live template exists for MATRIX_LANGUAGE. Mirrors the matching logic in
// Root.tsx (maintained by language+event+action+target+version, config by
// event+action+target).
export function deriveCell(summary: MailTemplateSummary, path: MatrixPath): { active: boolean; liveExists: boolean } {
  const { event, action, target } = path;
  const active = summary.config.some(
    (c: any) => c.event === event && c.action === action && c.target === target && c.active,
  );
  const liveExists = summary.maintained.some(
    (m: any) =>
      m.language === MATRIX_LANGUAGE &&
      m.event === event &&
      m.action === action &&
      m.target === target &&
      m.version === "live" &&
      !m.empty,
  );
  return { active, liveExists };
}

// The MATRIX_LANGUAGE paths that have a live version — the only ones whose
// content we bother fetching to count {% if %} structures.
export function deriveLivePaths(summary: MailTemplateSummary, allPaths: MatrixPath[]): MatrixPath[] {
  return allPaths.filter((p) => deriveCell(summary, p).liveExists);
}

// Jinja keywords / literals that are not "real" variables when scanning an
// `{% if %}` condition for the identifiers it references.
const CONDITION_KEYWORDS = new Set([
  "and",
  "or",
  "not",
  "in",
  "is",
  "none",
  "true",
  "false",
  "elif",
  "else",
]);

// Boilerplate variables that don't represent "real" business logic: multi-
// language wrappers (LANGUAGE) and the v3 footer/blog block toggle
// (V3_BOTTOM_BLOG). Compared upper-cased.
const IGNORED_CONDITION_VARS = new Set(["LANGUAGE"]);

// The non-boilerplate variables a single `{% if %}` condition tests. Strips
// string literals, then drops Jinja keywords and boilerplate vars (LANGUAGE,
// V3_BOTTOM_BLOG). A condition like `{% if LANGUAGE == 'nl' %}` yields nothing.
function conditionVariables(condition: string): string[] {
  const withoutStrings = condition.replace(/'[^']*'|"[^"]*"/g, " ");
  return (withoutStrings.match(/[A-Za-z_][A-Za-z0-9_.]*/g) ?? []).filter(
    (id) => !CONDITION_KEYWORDS.has(id.toLowerCase()) && !IGNORED_CONDITION_VARS.has(id.toUpperCase()),
  );
}

// Count the DISTINCT non-boilerplate variables that drive `{% if %}` conditionals
// across subject + body. The same variable used in several IFs counts once, and
// boilerplate-only conditionals (LANGUAGE / V3_BOTTOM_BLOG) contribute nothing.
// The `-?` tolerates whitespace-trim markers (`{%- if ... -%}`); the `s` flag
// lets a condition span newlines. SMS templates have no html; it coalesces to an
// empty string so they contribute their subject.
export function countIfs(subject?: string, html?: string): number {
  const text = `${subject ?? ""}\n${html ?? ""}`;
  const re = /\{%-?\s*if\b(.*?)-?%\}/gs;
  const variables = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    for (const v of conditionVariables(match[1])) variables.add(v);
  }
  return variables.size;
}

// Tiny concurrency limiter (no dependency). Route every network call through a
// shared instance so the total in-flight request count stays bounded no matter
// how many company queries and per-path content fetches are queued.
export function pLimit(max: number) {
  let active = 0;
  const queue: (() => void)[] = [];
  const next = () => {
    active--;
    queue.shift()?.();
  };
  return <T,>(fn: () => Promise<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const run = () => {
        active++;
        fn().then(resolve, reject).finally(next);
      };
      if (active < max) run();
      else queue.push(run);
    });
}
